import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@repo/design-system/utils";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogPortal,
  DialogTitle
} from "./dialog";

/**
 * A right-hand drawer built on the app's own dialog (`./dialog`, Radix's
 * `react-dialog`), for a drawer that other dialogs open on top of. It has the
 * look of {@link Drawer} (`./drawer`, `@medusajs/ui`) and shares its title,
 * description, close, root and portal with {@link Dialog}.
 *
 * WHEN TO USE WHICH
 *
 * - `Drawer` / `DrawerPanel` (`./drawer`) is the default for an off-canvas
 *   surface: filters, the phone search overlay, the cart slide-over.
 * - `NestableDrawer` (this file) only when a dialog built on `./dialog` (the
 *   product detail dialog, for one) has to open from inside the drawer. `Drawer`
 *   brings its own bundled copy of Radix, so its layer stack and focus trap know
 *   nothing about the app's dialogs: nested, Escape and an outside click dismiss
 *   both, the drawer's trap pulls focus out of the dialog above it (dead to the
 *   keyboard), and a dialog portalled to the body sits outside the trap. On the
 *   dialog's own package the two nest natively: one layer stack, one focus-scope
 *   chain, Escape closes the top-most layer only.
 *
 * Like `Drawer.Content` it is a portal, so a parent's `PII_MASK_CLASS` does not
 * reach it: a drawer that renders salon data puts the mask class on `className`
 * (ADR-0006).
 */
export function NestableDrawer({
  open,
  onOpenChange,
  children,
  className,
  ...contentProps
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
} & Omit<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
  "children"
>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay className="bg-ui-bg-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0" />
        <DialogPrimitive.Content
          className={cn(
            "bg-ui-bg-base shadow-elevation-modal border-ui-border-base fixed inset-y-2 flex w-full flex-1 flex-col rounded-lg border outline-none max-sm:inset-x-2 max-sm:w-[calc(100%-16px)] sm:right-2 sm:max-w-[560px]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-right-1/2 data-[state=open]:slide-in-from-right-1/2 duration-200",
            className
          )}
          {...contentProps}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

export {
  DialogClose as NestableDrawerClose,
  DialogDescription as NestableDrawerDescription,
  DialogTitle as NestableDrawerTitle
};
